import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { toast } from "sonner";
import { Loader2, TrendingUp, Users, Activity, CheckCircle2, Circle, Download } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function TrendsView() {
  const [start, setStart] = useState(daysAgo(13));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/trends", { params: { start, end } });
      setData(data);
    } catch (e) {
      toast.error("Could not load trends");
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (n) => {
    setStart(daysAgo(n));
    setEnd(today());
  };

  const totalReports = data?.daily.reduce((s, d) => s + d.reported, 0) ?? 0;
  const chartData =
    data?.daily.map((d) => ({ ...d, label: d.date.slice(5) })) ?? [];
  const complianceData =
    data?.daily.map((d) => ({
      label: d.date.slice(5),
      rate: data.total_members ? Math.round((d.reported / data.total_members) * 100) : 0,
    })) ?? [];

  return (
    <div>
      <UsagePanel />
      {/* Range picker */}
      <Card className="p-5 sm:p-6 rounded-xl mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>From</Label>
            <Input
              type="date"
              value={start}
              max={end}
              onChange={(e) => setStart(e.target.value)}
              data-testid="trends-start-input"
            />
          </div>
          <div className="space-y-2">
            <Label>To</Label>
            <Input
              type="date"
              value={end}
              max={today()}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="trends-end-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              [6, "7 days"],
              [13, "14 days"],
              [29, "30 days"],
            ].map(([n, label]) => (
              <Button
                key={n}
                variant="secondary"
                size="sm"
                className="rounded-full"
                data-testid={`trends-preset-${n}`}
                onClick={() => applyPreset(n)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <MiniStat icon={TrendingUp} label="Reports submitted" value={totalReports} tone="emerald" testid="trends-total-reports" />
            <MiniStat icon={Users} label="Active members" value={data?.per_member.length ?? 0} tone="sky" testid="trends-active-members" />
          </div>

          {/* Compliance trend */}
          <Card className="p-5 sm:p-6 rounded-xl mb-6" data-testid="trends-compliance-chart">
            <h3 className="font-heading text-lg font-semibold mb-1">Daily reporting rate</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Share of the team that posted a report each day — spot the weeks people slipped.
            </p>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={complianceData} margin={{ top: 4, right: 12, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(v) => [`${v}%`, "Reported"]}
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 13,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    name="Reported %"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "hsl(var(--primary))" }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Daily chart */}
          <Card className="p-5 sm:p-6 rounded-xl mb-6" data-testid="trends-daily-chart">
            <h3 className="font-heading text-lg font-semibold mb-1">Reports per day</h3>
            <p className="text-sm text-muted-foreground mb-5">
              How many people posted a report each day.
            </p>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 13,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="reported" name="Reports" fill="hsl(var(--foreground))" radius={[4, 4, 0, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Per-member chart */}
          <Card className="p-5 sm:p-6 rounded-xl" data-testid="trends-member-chart">
            <h3 className="font-heading text-lg font-semibold mb-1">Who reported</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Reports submitted by each team member over this period.
            </p>
            {data?.per_member.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">No reports in this period.</p>
            ) : (
              <div style={{ width: "100%", height: Math.max(200, (data?.per_member.length ?? 0) * 48) }}>
                <ResponsiveContainer>
                  <BarChart
                    layout="vertical"
                    data={data?.per_member}
                    margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--accent))" }}
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 12,
                        fontSize: 13,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Bar dataKey="reports" name="Reports" fill="hsl(var(--foreground))" radius={[0, 4, 4, 0]} maxBarSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone, testid }) {
  const toneMap = {
    emerald: "text-primary bg-primary/10",
    rose: "text-destructive bg-destructive/10",
    sky: "text-foreground bg-secondary",
  };
  return (
    <Card className="p-4 sm:p-5 rounded-xl" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${toneMap[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </Card>
  );
}

function UsagePanel() {
  const [period, setPeriod] = useState("week");
  const [rStart, setRStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 13); return d.toISOString().slice(0, 10); });
  const [rEnd, setREnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const params = period === "range" ? { period, start: rStart, end: rEnd } : { period };
        const { data } = await api.get("/usage", { params });
        setData(data);
      } catch (e) { /* ignore */ }
    })();
  }, [period, rStart, rEnd]);
  const rows = data?.rows || [];
  const updatedToday = period === "day" ? rows.filter((r) => r.used > 0).length : null;

  const CSV_MAP = { yes: "Yes", off: "Off", leave: "Leave", no: "No" };
  const q = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const downloadCsv = () => {
    if (!data) return;
    const lines = [["Member", "Role", ...data.dates].map(q).join(",")];
    rows.forEach((r) => {
      lines.push([q(r.name), q(r.role === "manager" ? "Manager" : "User"),
        ...data.dates.map((d) => q(CSV_MAP[r.by_date[d]] || "No"))].join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `report-status_${data.start}_to_${data.end}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report status CSV downloaded");
  };

  const cellIcon = (v) => {
    if (v === "yes") return <CheckCircle2 className="h-3 w-3 text-emerald-600" />;
    if (v === "off") return <span className="text-[9px] font-bold text-amber-600">O</span>;
    if (v === "leave") return <span className="text-[9px] font-bold text-sky-600">L</span>;
    return <Circle className="h-3 w-3 text-destructive" />;
  };

  return (
    <Card className="p-5 sm:p-6 rounded-xl mb-6" data-testid="usage-panel">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Activity className="h-5 w-5" /></div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold">App usage</h3>
          <p className="text-sm text-muted-foreground">Who's updating their report — and who isn't.</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"], ["range", "Date range"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={period === k ? "default" : "secondary"} className="rounded-full" onClick={() => setPeriod(k)} data-testid={`usage-period-${k}`}>{l}</Button>
          ))}
          <Button size="sm" variant="outline" className="rounded-full" onClick={downloadCsv} disabled={!data} data-testid="usage-download-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
        </div>
      </div>
      {period === "range" && (
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={rStart} max={rEnd} onChange={(e) => setRStart(e.target.value)} className="h-9 w-40" data-testid="usage-range-start" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)} className="h-9 w-40" data-testid="usage-range-end" /></div>
        </div>
      )}
      {!data ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
        <>
          <p className="text-xs text-muted-foreground mb-3 mt-2">{data.start === data.end ? data.start : `${data.start} → ${data.end}`}{updatedToday != null ? ` · ${updatedToday}/${rows.length} updated today` : ` · ${data.dates.length} days`}</p>
          <div className="space-y-1.5" data-testid="usage-list">
            {rows.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No members to show.</p>}
            {rows.map((r) => {
              const bad = r.pct === 0;
              const warn = r.pct > 0 && r.pct < 50;
              return (
                <div key={r.user_id} className={`rounded-lg border p-2.5 ${bad ? "border-destructive/40 bg-destructive/5" : "border-border"}`} data-testid={`usage-row-${r.user_id}`}>
                  <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(open === r.user_id ? null : r.user_id)} data-testid={`usage-toggle-${r.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{r.name} <span className="text-[11px] font-normal text-muted-foreground">· {r.role === "manager" ? "Manager" : "User"}</span></p>
                      <div className="h-1.5 rounded-full bg-secondary mt-1.5 overflow-hidden"><div className={`h-full ${bad ? "bg-destructive" : warn ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${r.pct}%` }} /></div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${bad ? "text-destructive" : warn ? "text-amber-600" : "text-emerald-600"}`}>{r.pct}%</p>
                      <p className="text-[11px] text-muted-foreground">{r.used} used · {r.missed} missed</p>
                    </div>
                  </button>
                  {open === r.user_id && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border" data-testid={`usage-detail-${r.user_id}`}>
                      {data.dates.map((d) => (
                        <span key={d} className="flex items-center gap-1 text-[10px] text-muted-foreground rounded px-1 py-0.5 bg-secondary/60" title={`${d} · ${r.by_date[d]}`}>
                          {cellIcon(r.by_date[d])}
                          {d.slice(5)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

